import { CalendarSubscriptionResponse } from "../model/backendTypes";
import { API_BASE } from "./apiBase";
import { apiFetch } from "./baseClient";

export type CalendarOverlayKind =
  | "changes"
  | "created"
  | "deleted"
  | "time-moved"
  | "resource-moved"
  | "details-changed";

export class CalendarApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "CalendarApiError";
    this.status = status;
  }
}

async function readCalendarError(resp: Response): Promise<CalendarApiError> {
  const rawText = await resp.text();

  let detail = rawText.trim();
  if (detail) {
    try {
      const parsed = JSON.parse(detail);
      if (parsed && typeof parsed === "object" && "detail" in parsed) {
        detail = String((parsed as { detail?: unknown }).detail ?? "").trim() || detail;
      }
    } catch {
      // keep raw text
    }
  }

  const message = detail || `HTTP ${resp.status}`;
  return new CalendarApiError(resp.status, message);
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, "");
}

function isLocalhostBase(baseUrl: string): boolean {
  try {
    const { hostname } = new URL(baseUrl);
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  } catch {
    return false;
  }
}

function getShareableCalendarBaseUrl(): string {
  const publicBase = window.__ENV__?.PUBLIC_BASE_URL?.trim();
  if (publicBase && !isLocalhostBase(publicBase)) {
    return normalizeBaseUrl(publicBase);
  }

  return normalizeBaseUrl(API_BASE);
}

export async function listCalendarSubscriptions(
  userId: number
): Promise<CalendarSubscriptionResponse[]> {
  const resp = await apiFetch(
    `/api/projects/calendar/subscriptions?user_id=${encodeURIComponent(String(userId))}`,
    {
      method: "GET",
      headers: { "cache-control": "no-store" },
    }
  );

  if (!resp.ok) {
    throw await readCalendarError(resp);
  }

  return (await resp.json()) as CalendarSubscriptionResponse[];
}

export async function subscribeProjectCalendar(
  userId: number,
  projectId: number,
  options?: {
    CalendarScope?: "full" | "filtered";
    filter_json?: Record<string, string[]>;
  }
): Promise<CalendarSubscriptionResponse> {
  const resp = await apiFetch("/api/projects/calendar/subscribe", {
    method: "POST",
    body: {
      user_id: userId,
      project_id: projectId,
      CalendarScope: options?.CalendarScope ?? "full",
      filter_json: options?.filter_json ?? null,
      provider: "local",
    } as any,
  });

  if (!resp.ok) {
    throw await readCalendarError(resp);
  }

  return (await resp.json()) as CalendarSubscriptionResponse;
}

export async function unsubscribeProjectCalendar(
  userId: number,
  projectId: number,
  subscriptionId?: number
): Promise<{ ok: boolean }> {
  const resp = await apiFetch("/api/projects/calendar/unsubscribe", {
    method: "POST",
    body: {
      user_id: userId,
      project_id: projectId,
      subscription_id: subscriptionId ?? null,
    } as any,
  });

  if (!resp.ok) {
    throw await readCalendarError(resp);
  }

  return (await resp.json()) as { ok: boolean };
}

export function getCalendarFeedUrl(publicToken: string): string {
  const baseUrl = getShareableCalendarBaseUrl();
  return `${baseUrl}/api/projects/calendar/feed/${encodeURIComponent(publicToken)}.ics`;
}

export function getCalendarWebcalUrl(publicToken: string): string {
  return getCalendarFeedUrl(publicToken).replace(/^https?:\/\//, "webcal://");
}

export function getCalendarOverlayFeedUrl(
  publicToken: string,
  overlayKind: CalendarOverlayKind
): string {
  const baseUrl = getShareableCalendarBaseUrl();
  return `${baseUrl}/api/projects/calendar/feed/${encodeURIComponent(
    publicToken
  )}/${encodeURIComponent(overlayKind)}.ics`;
}

export function getCalendarOverlayWebcalUrl(
  publicToken: string,
  overlayKind: CalendarOverlayKind
): string {
  return getCalendarOverlayFeedUrl(publicToken, overlayKind).replace(
    /^https?:\/\//,
    "webcal://"
  );
}

export function getResourceCalendarFeedUrl(
  projectId: number,
  dimensionName: string,
  pointName: string
): string {
  const params = new URLSearchParams({
    project_id: String(projectId),
    dimension_name: dimensionName,
    point_name: pointName,
  });

  const baseUrl = getShareableCalendarBaseUrl();
  return `${baseUrl}/api/projects/calendar/feed.ics?${params.toString()}`;
}

export function getResourceCalendarWebcalUrl(
  projectId: number,
  dimensionName: string,
  pointName: string
): string {
  return getResourceCalendarFeedUrl(projectId, dimensionName, pointName).replace(
    /^https?:\/\//,
    "webcal://"
  );
}
